import { ExtendedHighlight, HighlightBoundingRect } from "@/api/highlights";
import Fuse from "fuse.js";
import type { PDFDocumentProxy, PDFPageProxy } from "pdfjs-dist";
import type { TextItem } from "pdfjs-dist/types/src/display/api";

// Utility functions for PDF text search
export const getNextId = () => String(Math.random()).slice(2);

interface TextSearchMatch {
  pageIndex: number;
  text: string;
  score: number;
  position: {
    boundingRect: HighlightBoundingRect;
    rects: HighlightBoundingRect[];
    pageNumber: number;
  };
  usePdfCoordinates: boolean;
}

// A line is a group of text items at the same Y position
interface TextLine {
  items: TextItem[];
  text: string;
  y: number;
  minX: number;
  maxX: number;
  height: number;
}

// Bounds of a text item in viewport coordinates (after rotation transform)
interface TextItemBounds {
  x: number;
  y: number;
  width: number;
  height: number;
}

// Raw PDF bounds (before viewport transformation)
interface PdfBounds {
  x1: number;
  y1: number;
  x2: number;
  y2: number;
}

// Viewport type that may have convertToViewportRectangle and convertToViewportPoint methods
interface ViewportWithTransform {
  width: number;
  height: number;
  rotation?: number;
  convertToViewportRectangle?: (rect: number[]) => number[];
  convertToViewportPoint?: (x: number, y: number) => number[];
}

/**
 * Extract text item bounds, accounting for page rotation.
 *
 * KEY INSIGHT: item.width from PDF.js is ALREADY in PDF page units!
 * It's calculated as: sqrt(a² + b²) * textAdvance = scaleX * textAdvance
 * So we must NOT apply the full transform matrix to it again.
 *
 * SOLUTION:
 * 1. Use (e, f) as the origin point in PDF space
 * 2. Use normalized direction vectors to extend by item.width (already in page units)
 * 3. Use the height vector (c, d) directly for font height
 * 4. Convert all 4 corner POINTS to viewport space, then calculate AABB
 */
const getTextItemBounds = (
  item: TextItem,
  viewport: ViewportWithTransform
): TextItemBounds & { isViewportSpace: boolean; pdfBounds: PdfBounds } => {
  const { transform, width: itemWidth } = item;

  // Transform matrix: [a, b, c, d, e, f]
  // (e, f) is the text origin in PDF page space
  // (a, b) is the text direction vector (includes font size scale)
  // (c, d) is the perpendicular/height vector (includes font size scale)
  const [a, b, c, d, e, f] = transform;

  // Scale factors (essentially the font size)
  const scaleX = Math.sqrt(a * a + b * b);  // Scale along text direction
  const scaleY = Math.sqrt(c * c + d * d);  // Scale perpendicular (font height)

  // CRITICAL: item.width is ALREADY in PDF page units (already scaled)
  // So we use normalized direction vectors and multiply by item.width directly
  const textWidth = itemWidth || 0;
  const textHeight = scaleY;  // Font height in page units

  // Normalized direction vectors
  const dirX = scaleX > 0 ? a / scaleX : 1;  // Text direction X
  const dirY = scaleX > 0 ? b / scaleX : 0;  // Text direction Y
  const perpX = scaleY > 0 ? c / scaleY : 0; // Height direction X
  const perpY = scaleY > 0 ? d / scaleY : 1; // Height direction Y

  // Debug logging for target items
  const isDebugItem = item.str.includes("100") || item.str.includes("kW") || item.str.includes("kVA");

  // DEBUG: Check if viewport has conversion methods
  if (isDebugItem) {
    console.log(`[TextBounds DEBUG] viewport methods:`, {
      hasConvertToViewportPoint: !!viewport.convertToViewportPoint,
      hasConvertToViewportRectangle: !!viewport.convertToViewportRectangle,
      viewportWidth: viewport.width,
      viewportHeight: viewport.height,
    });
  }

  // Calculate the 4 corners in PDF page space
  // Origin is at (e, f)
  // Text extends along (dirX, dirY) by textWidth (already in page units)
  // Height extends along (perpX, perpY) by textHeight (font size)
  const p1: [number, number] = [e, f];  // Baseline start
  const p2: [number, number] = [e + dirX * textWidth, f + dirY * textWidth];  // Baseline end
  const p3: [number, number] = [e + perpX * textHeight, f + perpY * textHeight];  // Top start
  const p4: [number, number] = [e + dirX * textWidth + perpX * textHeight, f + dirY * textWidth + perpY * textHeight];  // Top end

  if (isDebugItem) {
    console.log(`[TextBounds] "${item.str}"`);
    console.log(`  transform: [${a.toFixed(2)}, ${b.toFixed(2)}, ${c.toFixed(2)}, ${d.toFixed(2)}, ${e.toFixed(2)}, ${f.toFixed(2)}]`);
    console.log(`  scaleX=${scaleX.toFixed(2)}, scaleY=${scaleY.toFixed(2)}`);
    console.log(`  textWidth=${textWidth.toFixed(2)}, textHeight=${textHeight.toFixed(2)}`);
    console.log(`  dir=(${dirX.toFixed(2)}, ${dirY.toFixed(2)}), perp=(${perpX.toFixed(2)}, ${perpY.toFixed(2)})`);
    console.log(`  PDF corners: p1=(${p1[0].toFixed(2)}, ${p1[1].toFixed(2)}), p2=(${p2[0].toFixed(2)}, ${p2[1].toFixed(2)}), p3=(${p3[0].toFixed(2)}, ${p3[1].toFixed(2)}), p4=(${p4[0].toFixed(2)}, ${p4[1].toFixed(2)})`);
  }

  // Convert corner POINTS to viewport space, then calculate AABB
  // This handles page rotation (0, 90, 180, 270) correctly
  if (viewport.convertToViewportPoint || viewport.convertToViewportRectangle) {
    // Helper to convert a single point to viewport space
    const toViewport = (p: [number, number]): [number, number] => {
      // Use native convertToViewportPoint if available
      if (viewport.convertToViewportPoint) {
        const result = viewport.convertToViewportPoint(p[0], p[1]);
        return [result[0], result[1]];
      }
      // Fallback: use convertToViewportRectangle with a degenerate rect
      if (viewport.convertToViewportRectangle) {
        const r = viewport.convertToViewportRectangle([p[0], p[1], p[0], p[1]]);
        return [r[0], r[1]];
      }
      return p;
    };

    // Convert all 4 corners to viewport space
    const v1 = toViewport(p1);
    const v2 = toViewport(p2);
    const v3 = toViewport(p3);
    const v4 = toViewport(p4);

    // Calculate AABB in viewport space (screen coordinates)
    const xs = [v1[0], v2[0], v3[0], v4[0]];
    const ys = [v1[1], v2[1], v3[1], v4[1]];

    const minX = Math.min(...xs);
    const maxX = Math.max(...xs);
    const minY = Math.min(...ys);
    const maxY = Math.max(...ys);

    if (isDebugItem) {
      console.log(`  Viewport corners: v1=(${v1[0].toFixed(2)}, ${v1[1].toFixed(2)}), v2=(${v2[0].toFixed(2)}, ${v2[1].toFixed(2)}), v3=(${v3[0].toFixed(2)}, ${v3[1].toFixed(2)}), v4=(${v4[0].toFixed(2)}, ${v4[1].toFixed(2)})`);
      console.log(`  final bounds: x=${minX.toFixed(2)}, y=${minY.toFixed(2)}, w=${(maxX - minX).toFixed(2)}, h=${(maxY - minY).toFixed(2)}`);
    }

    // Calculate PDF AABB from original corners (before viewport conversion)
    const pdfXs = [p1[0], p2[0], p3[0], p4[0]];
    const pdfYs = [p1[1], p2[1], p3[1], p4[1]];
    const pdfBounds: PdfBounds = {
      x1: Math.min(...pdfXs),
      y1: Math.min(...pdfYs),
      x2: Math.max(...pdfXs),
      y2: Math.max(...pdfYs),
    };

    return {
      x: minX,
      y: minY,
      width: maxX - minX,
      height: maxY - minY,
      isViewportSpace: true,
      pdfBounds,
    };
  }

  // Fallback for tests (PDF Space AABB) - only valid if no rotation
  const xs = [p1[0], p2[0], p3[0], p4[0]];
  const ys = [p1[1], p2[1], p3[1], p4[1]];
  const pdfBounds: PdfBounds = {
    x1: Math.min(...xs),
    y1: Math.min(...ys),
    x2: Math.max(...xs),
    y2: Math.max(...ys),
  };

  return {
    x: Math.min(...xs),
    y: Math.min(...ys),
    width: Math.max(...xs) - Math.min(...xs),
    height: Math.max(...ys) - Math.min(...ys),
    isViewportSpace: false,
    pdfBounds,
  };
};

export const normalizeText = (text: string) =>
  text.replace(/\s+/g, " ").trim().toLowerCase();

// Group text items into lines based on Y position
const groupIntoLines = (
  items: TextItem[],
  viewport: ViewportWithTransform,
  yTolerance: number = 3
): TextLine[] => {
  if (items.length === 0) return [];

  // Pre-compute bounds for all items
  const itemsWithBounds = items.map(item => ({
    item,
    bounds: getTextItemBounds(item, viewport)
  }));

  const isViewportSpace = itemsWithBounds[0]?.bounds.isViewportSpace ?? false;

  // Sort by Y (top to bottom on page), then by X (left to right)
  // In PDF space: Y increases upward, so sort descending
  // In viewport space: Y increases downward, so sort ascending
  const sorted = [...itemsWithBounds].sort((a, b) => {
    const yDiff = isViewportSpace
      ? a.bounds.y - b.bounds.y  // Viewport: ascending (top first)
      : b.bounds.y - a.bounds.y; // PDF: descending (top first)
    if (Math.abs(yDiff) > yTolerance) return yDiff;
    return a.bounds.x - b.bounds.x;
  });

  const lines: TextLine[] = [];
  let currentLineItemsWithBounds = [sorted[0]];
  let currentY = sorted[0].bounds.y;

  for (let i = 1; i < sorted.length; i++) {
    const entry = sorted[i];
    const itemY = entry.bounds.y;

    if (Math.abs(itemY - currentY) <= yTolerance) {
      currentLineItemsWithBounds.push(entry);
    } else {
      lines.push(createLine(currentLineItemsWithBounds));
      currentLineItemsWithBounds = [entry];
      currentY = itemY;
    }
  }

  if (currentLineItemsWithBounds.length > 0) {
    lines.push(createLine(currentLineItemsWithBounds));
  }

  return lines;
};

interface ItemWithBounds {
  item: TextItem;
  bounds: TextItemBounds & { isViewportSpace: boolean };
}

const createLine = (
  itemsWithBounds: ItemWithBounds[]
): TextLine => {
  // Sort by X position (left to right)
  const sorted = [...itemsWithBounds].sort((a, b) => a.bounds.x - b.bounds.x);
  const text = sorted.map((entry) => entry.item.str).join(" ");

  let minX = Infinity,
    maxX = -Infinity;
  let totalHeight = 0;

  for (const entry of sorted) {
    minX = Math.min(minX, entry.bounds.x);
    maxX = Math.max(maxX, entry.bounds.x + entry.bounds.width);
    totalHeight = Math.max(totalHeight, entry.bounds.height);
  }

  return {
    items: sorted.map(entry => entry.item),
    text,
    y: sorted[0].bounds.y,
    minX,
    maxX,
    height: totalHeight,
  };
};

// Get the first sentence fragment (up to first period) for anchoring search
const getFirstSentence = (text: string): string => {
  const periodIndex = text.indexOf(".");
  if (periodIndex !== -1 && periodIndex < 100) {
    return text.slice(0, periodIndex + 1);
  }
  return text.slice(0, 50);
};

// Get first few words for line-by-line anchoring
const getFirstWords = (text: string, count: number = 3): string[] => {
  return normalizeText(text).split(" ").slice(0, count);
};

// Calculate bounding rect from a range of lines (returns PDF coordinates)
const calculateBoundingRectFromLines = (
  lines: TextLine[],
  viewport: ViewportWithTransform,
  pageNumber: number
): HighlightBoundingRect => {
  let minX = Infinity,
    maxX = -Infinity,
    minY = Infinity,
    maxY = -Infinity;

  for (const line of lines) {
    minX = Math.min(minX, line.minX);
    maxX = Math.max(maxX, line.maxX);
    minY = Math.min(minY, line.y);
    maxY = Math.max(maxY, line.y + line.height);
  }

  // Return viewport coordinates (already converted via convertToViewportRectangle)
  const result = {
    x1: minX,
    y1: minY,
    x2: maxX,
    y2: maxY,
    width: viewport.width,
    height: viewport.height,
    pageNumber,
  };

  console.log(`[BoundingRect] Final viewport rect:`, result);

  return result;
};

// Find the line that contains the first words of the citation
const findAnchorLine = (lines: TextLine[], firstWords: string[]): number => {
  const searchPhrase = firstWords.join(" ");

  for (let i = 0; i < lines.length; i++) {
    const lineText = normalizeText(lines[i].text);
    if (lineText.includes(searchPhrase)) {
      return i;
    }
  }

  // Fallback: find line with most matching words
  let bestLine = -1;
  let bestScore = 0;

  for (let i = 0; i < lines.length; i++) {
    const lineText = normalizeText(lines[i].text);
    let score = 0;
    for (const word of firstWords) {
      if (lineText.includes(word)) score++;
    }
    if (score > bestScore) {
      bestScore = score;
      bestLine = i;
    }
  }

  return bestLine;
};

// Expand from anchor line to cover the full citation - but be conservative
const expandToFullCitation = (
  lines: TextLine[],
  anchorLineIndex: number,
  normalizedCitation: string
): { startLine: number; endLine: number } => {
  if (anchorLineIndex < 0) return { startLine: 0, endLine: 0 };

  let startLine = anchorLineIndex;
  let endLine = anchorLineIndex;

  // Build text from anchor
  let coveredText = normalizeText(lines[anchorLineIndex].text);

  // Check if we need to expand backward (citation might start on previous line)
  const citationStart = normalizedCitation.slice(0, 15);
  if (!coveredText.includes(citationStart) && anchorLineIndex > 0) {
    const prevText = normalizeText(lines[anchorLineIndex - 1].text);
    if (
      prevText.includes(citationStart) ||
      (prevText + " " + coveredText).includes(citationStart)
    ) {
      startLine = anchorLineIndex - 1;
      coveredText = prevText + " " + coveredText;
    }
  }

  // Get the last few significant words of the citation (skip short tokens)
  const citationWords = normalizedCitation
    .split(" ")
    .filter((w) => w.length > 2);
  const lastSignificantWords = citationWords.slice(-5);

  // Estimate max lines based on citation length (~50 chars per line)
  const estimatedMaxLines = Math.ceil(normalizedCitation.length / 50) + 1;
  const maxEndLine = Math.min(startLine + estimatedMaxLines, lines.length - 1);

  // Check if current coverage already has the ending
  const hasEnding = () => {
    let foundLastWords = 0;
    for (const word of lastSignificantWords) {
      if (coveredText.includes(word)) foundLastWords++;
    }
    return foundLastWords >= 4; // Need 4 out of 5
  };

  // Check before expanding - maybe we already have enough
  if (hasEnding()) {
    return { startLine, endLine };
  }

  // Expand forward until we find enough of the last words
  while (endLine < maxEndLine) {
    endLine++;
    coveredText += " " + normalizeText(lines[endLine].text);

    if (hasEnding()) {
      break;
    }
  }

  return { startLine, endLine };
};

export const findTextInPage = async (
  page: PDFPageProxy,
  searchText: string,
): Promise<TextSearchMatch[]> => {
  const textContent = await page.getTextContent();
  const textItems = textContent.items.filter(
    (item): item is TextItem => "str" in item
  );

  // Normalize search text for better matching
  const normalizedSearchText = normalizeText(searchText);
  const viewport = page.getViewport({ scale: 1 });

  // Debug viewport info
  const pageRotation = (page as unknown as { rotate?: number }).rotate || 0;
  console.log(`[Viewport] Page ${page.pageNumber}:`, {
    width: viewport.width,
    height: viewport.height,
    rotation: pageRotation,
    viewBox: (page as unknown as { view?: number[] }).view,
  });

  // For longer citations (>80 chars), use the sentence-based approach
  const isLongCitation = searchText.length > 80;
  console.log(`[findTextInPage] Search text length: ${searchText.length}, isLongCitation: ${isLongCitation}`);
  console.log(`[findTextInPage] Search text: "${searchText.substring(0, 50)}${searchText.length > 50 ? '...' : ''}"`);

  if (isLongCitation) {
    console.log(`[findTextInPage] Using LONG citation path (sentence-based)`);

    const lines = groupIntoLines(textItems, viewport);
    if (lines.length === 0) return [];

    // Step 1: Get first sentence/fragment to use for fuzzy search
    const firstSentence = getFirstSentence(searchText);

    // Step 2: Create sentence-based chunks for fuzzy search
    const sentences: { text: string; lineIndices: number[] }[] = [];
    let currentSentence = "";
    let currentLineIndices: number[] = [];

    for (let i = 0; i < lines.length; i++) {
      const lineText = lines[i].text;
      currentSentence += (currentSentence ? " " : "") + lineText;
      currentLineIndices.push(i);

      if (lineText.match(/[.!?;]\s*$/)) {
        sentences.push({
          text: currentSentence.trim(),
          lineIndices: [...currentLineIndices],
        });
        currentSentence = "";
        currentLineIndices = [];
      }
    }

    if (currentSentence.trim()) {
      sentences.push({
        text: currentSentence.trim(),
        lineIndices: [...currentLineIndices],
      });
    }

    // Step 3: Fuzzy search on sentences to find the start
    const fuse = new Fuse(sentences, {
      keys: ["text"],
      includeScore: true,
      threshold: 0.4,
      ignoreLocation: true,
    });

    let searchResults = fuse.search(firstSentence);
    if (searchResults.length === 0) {
      searchResults = fuse.search(normalizeText(firstSentence));
    }

    if (searchResults.length > 0) {
      const bestMatch = searchResults[0];

      // Step 4: Use first 2-3 words to find exact anchor line
      const firstWords = getFirstWords(searchText, 3);
      const anchorLineIndex = findAnchorLine(lines, firstWords);

      if (anchorLineIndex >= 0) {
        // Step 5: Expand forward/backward to cover full citation
        const { startLine, endLine } = expandToFullCitation(
          lines,
          anchorLineIndex,
          normalizedSearchText
        );

        const matchedLines = lines.slice(startLine, endLine + 1);
        const boundingRect = calculateBoundingRectFromLines(
          matchedLines,
          viewport,
          page.pageNumber
        );

        // Long citation path uses viewport coords from calculateBoundingRectFromLines
        // For rotated pages, this may need adjustment - for now use false
        const longCitationMatch: TextSearchMatch = {
          pageIndex: page.pageNumber - 1,
          text: matchedLines.map((l) => l.text).join(" "),
          score: bestMatch.score || 0,
          position: {
            boundingRect,
            rects: [boundingRect],
            pageNumber: page.pageNumber,
          },
          usePdfCoordinates: false,
        };
        return [longCitationMatch];
      }
    }
  }

  // Fallback: Original item-by-item search for short citations
  console.log(`[findTextInPage] Using SHORT citation path (item-by-item fuzzy search)`);
  const threshold = 0.5;
  const fuse = new Fuse(textContent.items, {
    keys: ["str"],
    includeScore: true,
    threshold,
  });

  let searchResults = fuse.search(searchText);

  if (searchResults.length === 0) {
    searchResults = fuse.search(normalizedSearchText);
  }

  // Helper function to find longest common substring
  const getLongestSubstring = (str1: string, str2: string): number => {
    let maxLength = 0;
    for (let i = 0; i < str1.length; i++) {
      for (let j = 0; j < str2.length; j++) {
        let length = 0;
        while (
          i + length < str1.length &&
          j + length < str2.length &&
          str1[i + length] === str2[j + length]
        ) {
          length++;
        }
        maxLength = Math.max(maxLength, length);
      }
    }
    return maxLength;
  };

  // Helper function to extend match to cover full citation
  const extendMatch = (
    startItem: TextItem,
    startIndex: number,
  ): {
    items: TextItem[];
    combinedText: string;
    boundingRect: HighlightBoundingRect;
    usePdfCoordinates: boolean;
  } => {
    const allItems = textContent.items;
    const extendedItems = [startItem];
    let combinedText = startItem.str;
    const normalizedOriginal = normalizeText(searchText);
    let currentLongestSubstring = getLongestSubstring(normalizeText(combinedText), normalizedOriginal);

    const viewport = page.getViewport({ scale: 1 });

    // Initial Bounds
    const startBounds = getTextItemBounds(startItem, viewport);

    // Track BOTH viewport bounds (for neighbor checks) and raw PDF bounds
    let minX = startBounds.x;
    let maxX = startBounds.x + startBounds.width;
    let minY = startBounds.y;
    let maxY = startBounds.y + startBounds.height;

    // Raw PDF bounds (un-rotated, bottom-left origin)
    let pdfMinX = startBounds.pdfBounds.x1;
    let pdfMaxX = startBounds.pdfBounds.x2;
    let pdfMinY = startBounds.pdfBounds.y1;
    let pdfMaxY = startBounds.pdfBounds.y2;

    // --- RELAXED NEIGHBOR CHECK (uses viewport coords) ---
    const isNeighbor = (itemBounds: TextItemBounds) => {
      const isSideBySide =
        itemBounds.y < maxY + 5 &&
        itemBounds.y + itemBounds.height > minY - 5 &&
        (Math.abs(itemBounds.x - maxX) < 50 || Math.abs(itemBounds.x + itemBounds.width - minX) < 50);

      const verticalGap = Math.min(
        Math.abs(itemBounds.y - maxY),
        Math.abs(itemBounds.y + itemBounds.height - minY)
      );

      const isVerticalNeighbor =
        verticalGap < 30 &&
        Math.abs(itemBounds.x - minX) < 200;

      return isSideBySide || isVerticalNeighbor;
    };

    // Backward Search
    let backIndex = startIndex - 1;
    let consecutiveSpacesBack = 0;
    while (backIndex >= 0 && consecutiveSpacesBack < 3 && startIndex - backIndex <= 10) {
      const prevItem = allItems[backIndex] as TextItem;
      const prevText = prevItem.str.trim();
      if (!prevText) { consecutiveSpacesBack++; backIndex--; continue; }
      consecutiveSpacesBack = 0;
      const testCombined = (prevText + " " + combinedText).trim();
      const newLongestSubstring = getLongestSubstring(normalizeText(testCombined), normalizedOriginal);
      const startsWithThis = normalizedOriginal.startsWith(normalizeText(prevText));
      const isConnector = prevText.length <= 1 && /^[-:.,\s]$/.test(prevText);

      if (newLongestSubstring > currentLongestSubstring || startsWithThis || isConnector) {
        const prevBounds = getTextItemBounds(prevItem, viewport);
        if (!isNeighbor(prevBounds)) break;
        extendedItems.unshift(prevItem);
        combinedText = testCombined;
        currentLongestSubstring = newLongestSubstring;
        // Update viewport bounds
        minX = Math.min(minX, prevBounds.x);
        maxX = Math.max(maxX, prevBounds.x + prevBounds.width);
        minY = Math.min(minY, prevBounds.y);
        maxY = Math.max(maxY, prevBounds.y + prevBounds.height);
        // Update PDF bounds
        pdfMinX = Math.min(pdfMinX, prevBounds.pdfBounds.x1);
        pdfMaxX = Math.max(pdfMaxX, prevBounds.pdfBounds.x2);
        pdfMinY = Math.min(pdfMinY, prevBounds.pdfBounds.y1);
        pdfMaxY = Math.max(pdfMaxY, prevBounds.pdfBounds.y2);
      } else { break; }
      backIndex--;
    }

    // Forward Search
    let currentIndex = startIndex + 1;
    let consecutiveSpaces = 0;
    while (currentIndex < allItems.length && consecutiveSpaces < 3) {
      const nextItem = allItems[currentIndex] as TextItem;
      if (!("str" in nextItem)) { currentIndex++; continue; }
      const nextText = nextItem.str.trim();
      if (!nextText) { consecutiveSpaces++; currentIndex++; continue; }
      consecutiveSpaces = 0;
      const testCombined = (combinedText + " " + nextText).trim();
      const newLongestSubstring = getLongestSubstring(normalizeText(testCombined), normalizedOriginal);
      const isConnector = nextText.length <= 1 && /^[-:.,\s]$/.test(nextText);

      if (newLongestSubstring > currentLongestSubstring || isConnector) {
        const nextBounds = getTextItemBounds(nextItem, viewport);
        if (!isNeighbor(nextBounds)) break;
        extendedItems.push(nextItem);
        combinedText = testCombined;
        currentLongestSubstring = newLongestSubstring;
        // Update viewport bounds
        minX = Math.min(minX, nextBounds.x);
        maxX = Math.max(maxX, nextBounds.x + nextBounds.width);
        minY = Math.min(minY, nextBounds.y);
        maxY = Math.max(maxY, nextBounds.y + nextBounds.height);
        // Update PDF bounds
        pdfMinX = Math.min(pdfMinX, nextBounds.pdfBounds.x1);
        pdfMaxX = Math.max(pdfMaxX, nextBounds.pdfBounds.x2);
        pdfMinY = Math.min(pdfMinY, nextBounds.pdfBounds.y1);
        pdfMaxY = Math.max(pdfMaxY, nextBounds.pdfBounds.y2);
        if (/[.!?]$/.test(nextText)) break;
      } else { break; }
      currentIndex++;
    }

    // --- FINAL RECT CALCULATION ---
    const pageRotation = (page as unknown as { rotate?: number }).rotate || 0;

    // Simple approach: use viewport coords with usePdfCoordinates=false
    const boundingRect: HighlightBoundingRect = {
      x1: minX,
      y1: minY,
      x2: maxX,
      y2: maxY,
      width: viewport.width,
      height: viewport.height,
      pageNumber: page.pageNumber,
    };

    // Calculate percentages for debugging
    const xPercent = ((minX + maxX) / 2 / viewport.width * 100).toFixed(1);
    const yPercent = ((minY + maxY) / 2 / viewport.height * 100).toFixed(1);

    console.log(`[ExtendMatch] Page rotation: ${pageRotation}°`);
    console.log(`[ExtendMatch]   Viewport size: ${viewport.width} x ${viewport.height}`);
    console.log(`[ExtendMatch]   Bounds: (${minX.toFixed(0)}, ${minY.toFixed(0)}) to (${maxX.toFixed(0)}, ${maxY.toFixed(0)})`);
    console.log(`[ExtendMatch]   Dimensions: width=${(maxX-minX).toFixed(0)}, height=${(maxY-minY).toFixed(0)}`);
    console.log(`[ExtendMatch]   Position as %: x=${xPercent}%, y=${yPercent}%`);

    return {
      items: extendedItems,
      combinedText,
      boundingRect: {
        ...boundingRect,
        width: Math.abs(boundingRect.width),
        height: Math.abs(boundingRect.height),
        x1: Math.min(boundingRect.x1, boundingRect.x2),
        y1: Math.min(boundingRect.y1, boundingRect.y2),
        x2: Math.max(boundingRect.x1, boundingRect.x2),
        y2: Math.max(boundingRect.y1, boundingRect.y2),
      },
      usePdfCoordinates: false, // ALWAYS false - our coords are viewport space
    };
  };

  // Process search results
  if (searchResults.length > 0) {
    console.log(
      `Found ${searchResults.length} matches for "${searchText}" on page ${page.pageNumber}`,
    );
  }

  const significantTokens = normalizedSearchText
    .split(" ")
    .filter((token) => token.length > 2);
  const minTokenMatches = significantTokens.length > 2
    ? Math.ceil(significantTokens.length * 0.6)
    : significantTokens.length;

  const candidateMatches = searchResults.map((result) => {
    const item = result.item as TextItem;
    const itemIndex = textContent.items.indexOf(item);
    console.log(`[findTextInPage] Processing Fuse result: "${item.str}" at index ${itemIndex}`);
    const extended = extendMatch(item, itemIndex);

    console.log(`[findTextInPage] extendMatch returned boundingRect:`, JSON.stringify(extended.boundingRect));

    const normalizedCombinedText = normalizeText(extended.combinedText);
    const matchedTokens = significantTokens.reduce((count, token) =>
      count + (normalizedCombinedText.includes(token) ? 1 : 0),
    0);

    const match: TextSearchMatch = {
      pageIndex: page.pageNumber - 1,
      text: extended.combinedText,
      score: result.score || 0,
      position: {
        boundingRect: extended.boundingRect,
        rects: [extended.boundingRect],
        pageNumber: page.pageNumber,
      },
      usePdfCoordinates: extended.usePdfCoordinates,
    };

    console.log(`[findTextInPage] Created match with position.boundingRect:`, JSON.stringify(match.position.boundingRect));
    console.log(`[findTextInPage] usePdfCoordinates: ${match.usePdfCoordinates}`);
    console.log(`[findTextInPage] matchedTokens=${matchedTokens}, minRequired=${minTokenMatches}`);

    return { match, matchedTokens, normalizedCombinedText };
  });

  if (candidateMatches.length === 0) {
    return [];
  }

  let filteredMatches = candidateMatches.filter(({ matchedTokens }) => {
    if (significantTokens.length === 0) return true;
    return matchedTokens >= minTokenMatches;
  });

  if (filteredMatches.length === 0) {
    filteredMatches = candidateMatches.filter(({ normalizedCombinedText }) =>
      normalizedCombinedText.includes(normalizedSearchText),
    );
  }

  if (filteredMatches.length === 0) {
    filteredMatches = [candidateMatches[0]];
  }

  return filteredMatches.map(({ match }) => match);
};

export const searchTextInDocument = async (
  pdfDocument: PDFDocumentProxy | undefined,
  text: string,
  specificPage?: number | null,
): Promise<ExtendedHighlight[]> => {
  if (!pdfDocument || !text) return [];

  const matches: TextSearchMatch[] = [];
  let startPage: number;
  let endPage: number;

  // Handle null page - search all pages
  if (specificPage === null || specificPage === undefined) {
    startPage = 1;
    endPage = pdfDocument.numPages;
  } else {
    startPage = specificPage;
    endPage = specificPage;
  }

  for (let i = startPage; i <= endPage; i++) {
    const page = await pdfDocument.getPage(i);
    const pageMatches = await findTextInPage(page, text);
    if (pageMatches.length > 0) {
      console.log(`DEBUG: Found ${pageMatches.length} matches on page ${i}`);
      matches.push(...pageMatches);
      // For document-wide search, we can break on first match to avoid duplicates
      if (specificPage === null || specificPage === undefined) {
        break; // Stop at first page with matches for document-wide citations
      }
    }
  }

  if (matches.length > 0) {
    const highlights = matches.map((match) => {
      const { boundingRect } = match.position;

      // Debug log the final bounding rect being used
      console.log(`[Highlight] Creating highlight with rect:`, boundingRect);
      console.log(`[Highlight] usePdfCoordinates: ${match.usePdfCoordinates}`);

      // Use the flag from extendMatch:
      // - For rotated pages (90°/270°): usePdfCoordinates=true with raw PDF coords
      // - For non-rotated pages: usePdfCoordinates=false with viewport coords
      const highlight: ExtendedHighlight = {
        id: `search-${getNextId()}`,
        type: "text" as const,
        position: {
          boundingRect,
          rects: [boundingRect],
          usePdfCoordinates: match.usePdfCoordinates,
        },
        // content carries the actual PDF text that matched so downstream
        // consumers (e.g. scroll-to-highlight positioning) can reason about
        // what was found without re-deriving it from coordinates.
        content: { text: match.text },
        comment: { text: `Found: ${match.text}`, emoji: "🔍" },
      };
      return highlight;
    });

    // DIAGNOSTIC: Add a test highlight at (0,0) to verify coordinate origin
    const firstMatch = matches[0];
    const testRect = {
      x1: 0,
      y1: 0,
      x2: 100,
      y2: 40,
      width: firstMatch.position.boundingRect.width,
      height: firstMatch.position.boundingRect.height,
      pageNumber: firstMatch.position.boundingRect.pageNumber,
    };
    console.log(`[DIAGNOSTIC] Test highlight at (0,0,100,40) with usePdfCoordinates: ${firstMatch.usePdfCoordinates}`);
    highlights.push({
      id: `test-origin-${getNextId()}`,
      type: "text" as const,
      position: {
        boundingRect: testRect,
        rects: [testRect],
        usePdfCoordinates: firstMatch.usePdfCoordinates,
      },
      comment: { text: "TEST: Should be at TOP-LEFT (if usePdfCoordinates=false) or PDF origin (if true)", emoji: "📍" },
    });

    return highlights;
  }

  return [];
};
